import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    RadioGroup, FormControlLabel, Radio, FormLabel, FormControl, Box,
    Select, MenuItem, Checkbox, ListItemText, OutlinedInput, InputLabel, Typography,
} from '@mui/material';
import { INotice, INoticeRequest, NoticeAudienceType } from '../../interfaces/notice.interface';
import { Department } from '../../interfaces/department.interface';
import { DepartmentService } from '../../services/department.service';
import { NoticeService } from '../../services/notice.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    notice?: INotice | null;
    onClose: () => void;
    onSaved: () => void;
}

const toLocalInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NoticeForm: React.FC<Props> = ({ open, notice, onClose, onSaved }) => {
    const toast = useToast();
    const { t } = useTranslation(['notice', 'common']);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [publishAt, setPublishAt] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [audienceType, setAudienceType] = useState<NoticeAudienceType>('ALL');
    const [departmentIds, setDepartmentIds] = useState<string[]>([]);
    const [attachment, setAttachment] = useState<File | null>(null);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        DepartmentService.getDepartmentsByInstitution()
            .then(r => setDepartments(r.content))
            .catch(err => toast.showApiError(err, t('noticeForm.errors.loadDepartments')));
    }, [open]);

    useEffect(() => {
        if (notice) {
            setTitle(notice.title);
            setBody(notice.body);
            setPublishAt(toLocalInput(notice.publishAt));
            setExpiresAt(toLocalInput(notice.expiresAt));
            setAudienceType(notice.audienceType);
            setDepartmentIds(notice.departmentIds || []);
        } else {
            setTitle('');
            setBody('');
            setPublishAt('');
            setExpiresAt('');
            setAudienceType('ALL');
            setDepartmentIds([]);
        }
        setAttachment(null);
    }, [notice, open]);

    const handleSubmit = async () => {
        if (!title.trim() || !body.trim() || !expiresAt) {
            toast.showError(t('noticeForm.errors.required'));
            return;
        }
        if (audienceType === 'DEPARTMENTS' && departmentIds.length === 0) {
            toast.showError(t('noticeForm.errors.selectDepartment'));
            return;
        }
        const request: INoticeRequest = {
            title: title.trim(),
            body,
            publishAt: publishAt ? new Date(publishAt).toISOString() : null,
            expiresAt: new Date(expiresAt).toISOString(),
            audienceType,
            departmentIds: audienceType === 'DEPARTMENTS' ? departmentIds : [],
        };
        setSaving(true);
        try {
            if (notice) {
                await NoticeService.update(notice.id, institutionId, request);
            } else {
                await NoticeService.create(institutionId, request, attachment);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('noticeForm.errors.save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{notice ? t('noticeForm.editTitle') : t('noticeForm.newTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label={t('noticeForm.titleLabel')} value={title} onChange={e => setTitle(e.target.value)} fullWidth required inputProps={{ maxLength: 200 }} />
                    <TextField label={t('noticeForm.bodyLabel')} value={body} onChange={e => setBody(e.target.value)} fullWidth required multiline minRows={4} inputProps={{ maxLength: 10000 }} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label={t('noticeForm.publishAtLabel')} type="datetime-local" value={publishAt} onChange={e => setPublishAt(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                        <TextField label={t('noticeForm.expiresAtLabel')} type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} InputLabelProps={{ shrink: true }} required fullWidth />
                    </Box>
                    <FormControl>
                        <FormLabel>{t('noticeForm.audience')}</FormLabel>
                        <RadioGroup row value={audienceType} onChange={e => setAudienceType(e.target.value as NoticeAudienceType)}>
                            <FormControlLabel value="ALL" control={<Radio />} label={t('noticeForm.allEmployees')} />
                            <FormControlLabel value="DEPARTMENTS" control={<Radio />} label={t('noticeForm.selectedDepartments')} />
                        </RadioGroup>
                    </FormControl>
                    {audienceType === 'DEPARTMENTS' && (
                        <FormControl fullWidth>
                            <InputLabel>{t('noticeForm.departments')}</InputLabel>
                            <Select
                                multiple
                                value={departmentIds}
                                onChange={e => setDepartmentIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                input={<OutlinedInput label={t('noticeForm.departments')} />}
                                renderValue={(selected) => departments.filter(d => selected.includes(d.id)).map(d => d.name).join(', ')}
                            >
                                {departments.map(d => (
                                    <MenuItem key={d.id} value={d.id}>
                                        <Checkbox checked={departmentIds.includes(d.id)} />
                                        <ListItemText primary={d.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {!notice && (
                        <Box>
                            <Button variant="outlined" component="label">
                                {attachment ? t('noticeForm.attachment', { name: attachment.name }) : t('noticeForm.addAttachment')}
                                <input hidden type="file" onChange={e => setAttachment(e.target.files?.[0] || null)} />
                            </Button>
                            {attachment && (
                                <Button size="small" onClick={() => setAttachment(null)} sx={{ ml: 1 }}>{t('common:actions.remove')}</Button>
                            )}
                        </Box>
                    )}
                    {notice && notice.hasAttachment && (
                        <Typography variant="caption" color="text.secondary">
                            {t('noticeForm.existingAttachment', { name: notice.attachmentOriginalName })}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}>
                    {notice ? t('common:actions.save') : t('noticeForm.publish')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NoticeForm;
